import { StopIcon } from '@primer/octicons-react';
import { Suspense } from 'react';
import { graphql, useFragment, useQueryLoader } from 'react-relay';

import { BrandMessageTaskCreateDialog_brandMessagePreviewQuery } from '../../../relay/__generated__/BrandMessageTaskCreateDialog_brandMessagePreviewQuery.graphql';
import { BrandMessageTaskCreateDialog_brandMessageTaskCreateMutation } from '../../../relay/__generated__/BrandMessageTaskCreateDialog_brandMessageTaskCreateMutation.graphql';
import { BrandMessageTaskCreateDialog_brandMessageTemplate$key } from '../../../relay/__generated__/BrandMessageTaskCreateDialog_brandMessageTemplate.graphql';
import { numberWithCommas } from '../../../utils/number';
import BrandMessagePreview from '../../brandMessageTask/BrandMessagePreview';
import Button from '../../core/Button';
import Card from '../../core/Card';
import DatetimeField from '../../core/DatetimeField';
import DescriptionList from '../../core/DescriptionList';
import Dialog, { DialogProps } from '../../core/Dialog';
import DialogButton from '../../core/DialogButton';
import Flash from '../../core/Flash';
import ErrorBoundary from '../../core/ErrorBoundary';
import ErrorState from '../../core/ErrorState';
import FormLayout from '../../core/FormLayout';
import Grid from '../../core/Grid';
import Label from '../../core/Label';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NonFieldError from '../../core/NonFieldError';
import NumberField from '../../core/NumberField';
import PreloadedQueryRenderer from '../../core/PreloadedQueryRenderer';
import SQLEditorField from '../../core/SQLEditorField';
import Spinner from '../../core/Spinner';
import Stack from '../../core/Stack';
import Text from '../../core/Text';
import TextField from '../../core/TextField';
import TextareaField from '../../core/TextareaField';
import View from '../../core/View';

const BrandMessageTaskCreateDialog_brandMessageTemplate = graphql`
  fragment BrandMessageTaskCreateDialog_brandMessageTemplate on BrandMessageTemplate {
    id
    content
    parameterNames
  }
`;

const brandMessagePreviewForBrandMessageTaskCreateDialog = graphql`
  query BrandMessageTaskCreateDialog_brandMessagePreviewQuery($templateId: ID!, $query: String = null) {
    brandMessagePreview(templateId: $templateId, query: $query) {
      targetCount
      ...BrandMessagePreview_brandMessagePreview
    }
  }
`;

type Props = { brandMessageTemplate: BrandMessageTaskCreateDialog_brandMessageTemplate$key } & DialogProps &
  Partial<
    Pick<MutationFormikProps<BrandMessageTaskCreateDialog_brandMessageTaskCreateMutation>, 'config' | 'initialValues'>
  >;

const BrandMessageTaskCreateDialog = ({ config, brandMessageTemplate, initialValues = {}, ...props }: Props) => {
  const { id, parameterNames } = useFragment(BrandMessageTaskCreateDialog_brandMessageTemplate, brandMessageTemplate);

  const [previewQueryReference, loadPreviewQuery] =
    useQueryLoader<BrandMessageTaskCreateDialog_brandMessagePreviewQuery>(
      brandMessagePreviewForBrandMessageTaskCreateDialog,
    );

  return (
    <Dialog {...props}>
      <Dialog.Header>
        <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>브랜드 메세지 제작하기</Text>
      </Dialog.Header>
      <MutationFormik<BrandMessageTaskCreateDialog_brandMessageTaskCreateMutation>
        mutation={graphql`
          mutation BrandMessageTaskCreateDialog_brandMessageTaskCreateMutation($input: BrandMessageTaskCreateInput!) {
            brandMessageTaskCreate(input: $input) {
              id
            }
          }
        `}
        initialValues={{ templateId: id, title: '', query: '', startAt: new Date().toISOString(), ...initialValues }}
        config={config}
        enableReinitialize={false}
      >
        {({ values, errors, submitForm }, { scrollContainerRef, nonFieldErrorRef }) => {
          return (
            <>
              <Dialog.Body ref={scrollContainerRef}>
                <FormLayout>
                  <NonFieldError ref={nonFieldErrorRef} />
                  {errors.query ? (
                    // 필드 오류가 나면 MutationFormik 이 맨 위로 스크롤하는데 SQL 칸은 맨 아래라
                    // 정작 오류가 안 보인다. 위에서도 원인을 알 수 있게 첫 줄만 요약해 띄운다
                    // (자세한 안내와 예시는 SQL 칸 아래에 그대로 나온다).
                    <Flash variant={'danger'}>
                      <StopIcon />
                      {String(errors.query).split('\n')[0]}
                    </Flash>
                  ) : null}
                  <TextField
                    label={'제목'}
                    required
                    size={'large'}
                    placeholder={'제목 입력'}
                    name={'title'}
                    autoComplete={'off'}
                  />
                  <TextareaField label={'설명'} placeholder={'설명 입력'} name={'description'} autoComplete={'off'} />
                  <DatetimeField
                    label={'시작일'}
                    size={'large'}
                    required
                    name={'startAt'}
                    datePickerProps={{ placeholder: '시작일 선택' }}
                  />
                  <NumberField
                    label={'주기'}
                    placeholder={'주기 입력'}
                    name={'period'}
                    size={'large'}
                    caption={'입력하지 않으면 시작일에 한 번 발송됩니다'}
                  />
                  <View>
                    <Text fontSize={1} fontWeight={'bold'}>
                      SQL *
                    </Text>
                    <View my={1} borderStyle={'none'} backgroundColor={'canvas.inset'} borderRadius={2} px={3} py={2}>
                      <Grid wrap={false}>
                        <Grid.Unit size={'min'}>
                          <View width={30}>
                            <Text fontSize={0} fontWeight={'bold'}>
                              변수
                            </Text>
                          </View>
                        </Grid.Unit>
                        <Grid.Unit size={'min'}>
                          <Stack gapX={1} gapY={1} wrap={true}>
                            {parameterNames.map((name) => (
                              <Stack.Item key={name}>
                                <Label variant={'default'}>{name}</Label>
                              </Stack.Item>
                            ))}
                          </Stack>
                        </Grid.Unit>
                      </Grid>
                    </View>
                    <SQLEditorField
                      label={'SQL'}
                      labelConfig={{ visuallyHidden: true }}
                      name={'query'}
                      height={300}
                      required
                    />
                  </View>
                </FormLayout>
              </Dialog.Body>
              <Dialog.Footer>
                <DialogButton
                  variant={'primary'}
                  size={'large'}
                  renderDialog={({ isOpen, closeDialog }) => (
                    <Dialog isOpen={isOpen} onDismiss={closeDialog}>
                      <ErrorBoundary
                        skipSentry
                        key={previewQueryReference?.fetchKey}
                        fallback={({ parsedGraphQLError }) => {
                          return (
                            <View sx={{ padding: 5 }}>
                              <ErrorState
                                description={
                                  '작성된 SQL도 다시 확인해주세요' +
                                  (parsedGraphQLError?.[0].message ? `\n${parsedGraphQLError?.[0].message}` : '')
                                }
                              />
                            </View>
                          );
                        }}
                      >
                        <Dialog.Header>
                          <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>브랜드 메세지 미리보기</Text>
                        </Dialog.Header>
                        <Dialog.Body>
                          {previewQueryReference ? (
                            <Suspense
                              fallback={
                                <View py={10}>
                                  <Spinner />
                                </View>
                              }
                            >
                              <PreloadedQueryRenderer
                                query={brandMessagePreviewForBrandMessageTaskCreateDialog}
                                queryReference={previewQueryReference}
                              >
                                {({ brandMessagePreview }) => {
                                  return (
                                    <View>
                                      <View width={320} mx={'auto'}>
                                        <BrandMessagePreview brandMessagePreview={brandMessagePreview} />
                                      </View>
                                      <Card sx={{ padding: 4, mt: 3 }}>
                                        <DescriptionList
                                          item={brandMessagePreview}
                                          itemDescriptions={{
                                            targetCount: {
                                              title: '발송 대상',
                                              renderValue: ({ targetCount }) => (
                                                <Text fontSize={1}>{numberWithCommas(targetCount)}명</Text>
                                              ),
                                            },
                                          }}
                                          picks={['targetCount']}
                                          titleUnitSize={1 / 4}
                                          descriptionUnitSize={3 / 4}
                                        />
                                      </Card>
                                    </View>
                                  );
                                }}
                              </PreloadedQueryRenderer>
                            </Suspense>
                          ) : null}
                        </Dialog.Body>
                        <Dialog.Footer>
                          <Button
                            onClick={() => {
                              // 제출 결과(특히 실패 안내)는 뒤 화면에 뜬다. 미리보기가 덮고 있으면
                              // 원인을 볼 수 없어 재시도만 하게 되므로 함께 닫는다.
                              closeDialog();
                              submitForm();
                            }}
                            variant={'primary'}
                            size={'large'}
                          >
                            제작하기
                          </Button>
                        </Dialog.Footer>
                      </ErrorBoundary>
                    </Dialog>
                  )}
                  onClick={() => {
                    loadPreviewQuery({ query: values.query, templateId: id });
                  }}
                >
                  미리보기
                </DialogButton>
              </Dialog.Footer>
            </>
          );
        }}
      </MutationFormik>
    </Dialog>
  );
};

export default BrandMessageTaskCreateDialog;
